import type { CompositeFilterDescriptor } from "@tql/ui-components-react/kendo/data-query";
import { filterBy } from "@tql/ui-components-react/kendo/data-query";
import { RadioGroup } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { useCallback, useState } from "react";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import styles from "./export-report-modal.module.css";

type ColumnFilterOption = "current" | "none";
type ColumnOption = "selected" | "all";

const COLUMN_FILTER_OPTIONS = [
	{ value: "current", label: "Current Filters" },
	{ value: "none", label: "No Filters" },
];

const COLUMN_OPTIONS = [
	{ value: "selected", label: "Selected" },
	{ value: "all", label: "All Available" },
];

function escapeCsvValue(value: unknown): string {
	if (value === null || value === undefined) {
		return "";
	}
	if (typeof value === "object") {
		return "";
	}
	const str = String(value as string | number | boolean);
	if (str.includes(",") || str.includes('"') || str.includes("\n")) {
		return `"${str.replaceAll('"', '""')}"`;
	}
	return str;
}

function buildCsv(
	data: Record<string, unknown>[],
	columns: BoardColumn[],
	formatValue?: (field: string, value: unknown) => unknown,
): string {
	const headers = columns.map((col) => escapeCsvValue(col.title ?? col.field));
	const rows = data.map((row) =>
		columns
			.map((col) => {
				const rawValue = col.field ? row[col.field] : "";
				const value =
					formatValue && col.field
						? formatValue(col.field, rawValue)
						: rawValue;
				return escapeCsvValue(value);
			})
			.join(","),
	);
	return [headers.join(","), ...rows].join("\n");
}

function downloadCsv(csv: string, filename: string): void {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}

type ExportReportModalProps = {
	onClose: () => void;
	data: Record<string, unknown>[];
	allColumns: BoardColumn[];
	selectedColumns: BoardColumn[];
	filter: CompositeFilterDescriptor | undefined;
	filename?: string;
	formatValue?: (field: string, value: unknown) => unknown;
};

export function ExportReportModal({
	onClose,
	data,
	allColumns,
	selectedColumns,
	filter,
	filename = "report-export.csv",
	formatValue,
}: Readonly<ExportReportModalProps>) {
	const [columnFilterOption, setColumnFilterOption] =
		useState<ColumnFilterOption>("current");
	const [columnOption, setColumnOption] = useState<ColumnOption>("selected");

	const handleExport = useCallback(() => {
		const columns =
			columnOption === "selected"
				? selectedColumns.filter((col) => col.show)
				: allColumns;

		const filteredData =
			columnFilterOption === "current" && filter
				? filterBy(data, filter)
				: data;

		const csv = buildCsv(filteredData, columns, formatValue);
		downloadCsv(csv, filename);
		onClose();
	}, [
		columnOption,
		columnFilterOption,
		selectedColumns,
		allColumns,
		data,
		filter,
		filename,
		onClose,
		formatValue,
	]);

	return (
		<TraxModal
			confirmText={"Export"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={handleExport}
			showConfirmationButtons={true}
			title={"Export Report"}
			width={"300px"}
		>
			<div className={styles.exportReportModalContent}>
				<div className={styles.exportReportModalSection}>
					<Label>Column Filters</Label>
					<RadioGroup
						data={COLUMN_FILTER_OPTIONS}
						layout={"horizontal"}
						onChange={(e: { value: string }) =>
							setColumnFilterOption(e.value as ColumnFilterOption)
						}
						value={columnFilterOption}
					/>
				</div>
				<div className={styles.exportReportModalSection}>
					<Label>Columns</Label>
					<RadioGroup
						data={COLUMN_OPTIONS}
						layout={"horizontal"}
						onChange={(e: { value: string }) =>
							setColumnOption(e.value as ColumnOption)
						}
						value={columnOption}
					/>
				</div>
			</div>
		</TraxModal>
	);
}
